// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Img, { ImgProps } from "@insite/mobius/Img";
// import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import LazyImage, { LazyImageProps } from "@ces/MobiusOverrides/LazyImage";
import LazyPicture from "@ces/MobiusOverrides/LazyImage/LazyPicture";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

import * as cssLinter from "css";
import { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
    altText = "altText",
    imageLink = "imageLink",
    // XNG-Change
    largeImagePath = "largeImagePath",
    mediumImagePath = "mediumImagePath",
    smallImagePath = "smallImagePath",
    openInNewTab = "openInNewTab",
    disableLazyLoad = "disableLazyLoad",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.customCSS]: string;
        // XNG-Change
        [fields.largeImagePath]: string;
        [fields.mediumImagePath]: string;
        [fields.smallImagePath]: string;
        [fields.openInNewTab]: boolean;
        [fields.disableLazyLoad]: boolean;
    };
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
}

export const imageStyles: ImageStyles = {
    wrapper: {
        css: css`
            img {
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
    image: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
    img: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
};

const styles = imageStyles;

const Image: FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.imageLink);
    const image = (
        <LazyPicture
            smallImageSrc={fields.smallImagePath}
            mediumImageSrc={fields.mediumImagePath}
            largeImageSrc={fields.largeImagePath}
            altText={fields.altText}
            disableLazyLoad={fields.disableLazyLoad}
        />
    );

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="img-wrapper" {...styles.wrapper}>
                {url ? (
                    <Link
                        className="img-link"
                        href={url}
                        target={fields.openInNewTab ? "_blank" : "_self"}
                        {...styles.imageLink}
                        aria-label={`${fields.altText}`}
                    >
                        {image}
                    </Link>
                ) : (
                    image
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.img-wrapper {
}

.img-link {
}

.img {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Image,
    definition: {
        group: "Basic",
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.largeImagePath,
                displayName: "Large Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },

            {
                name: fields.mediumImagePath,
                displayName: "Medium Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.smallImagePath,
                displayName: "Small Image Path",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.imageLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.openInNewTab,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.disableLazyLoad,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: advancedTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
